import GuideEditorShell from './GuideEditorShell';
import HeroImageField from './HeroImageField';
import HtmlBodyEditor from './HtmlBodyEditor';
import ImportReport from './ImportReport';
import { type ImportReportData, formatCost, reportFromDone } from './importReportData';
import StructuredBodyEditor from './StructuredBodyEditor';
import CRMTabs from '@components/Common/CRMTabs';
import DetailSection from '@components/Common/DetailSection';
import FileUpload from '@components/Common/FileUpload/FileUpload';
import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import { PageHero } from '@components/Common/Panel';
import { useToast } from '@components/Common/Toast/ToastProvider';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Alert, AlertTitle, Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useBeforeUnloadWhile } from '../useBeforeUnloadWhile';
import { useSlugAvailability } from '../useSlugAvailability';

const PAGE_TYPE_OPTIONS = [
  { value: 'skill-guide', label: 'Academy guide (skill)' },
  { value: 'hiring-guide', label: 'Hiring guide' },
  { value: 'career-guide', label: 'Career guide' },
];
const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

/**
 * Extraction stages, streamed by the /guides/extract route as it works. Ordered as they occur,
 * so the panel can mark earlier stages complete without guessing at timings.
 */
const STAGES: { key: string; label: string; detail: string }[] = [
  { key: 'reading', label: 'Reading your document', detail: 'Pulling the text out of the file' },
  /*
   * "usually under a minute" was written when this mapped a document onto fields. Generating a
   * styled HTML body takes four to six minutes, and a retry doubles it, so the screenshot of a
   * healthy run said "usually under a minute" beside "266s elapsed". A progress panel that
   * contradicts its own timer teaches the operator to distrust it, which is worse than saying
   * nothing.
   */
  { key: 'extracting', label: 'Structuring it into a guide', detail: 'The slow part: usually four to six minutes' },
  /*
   * The generator validates its own answer and retries once inside the 'extracting' stage, so a
   * slow run there is that retry, not a hang. This stage is the console's own comparison against
   * the source, which now reports rather than triggering a second paid attempt.
   */
  { key: 'checking', label: 'Checking nothing was missed', detail: 'Comparing every point against your source' },
];

/** Source input modes. Order must match SOURCE_TABS. */
const MODE_UPLOAD = 0;
const MODE_PASTE = 1;
const SOURCE_TABS = [
  { label: 'Upload document', icon: <UploadFileOutlinedIcon fontSize="small" /> },
  { label: 'Paste text', icon: <ContentPasteOutlinedIcon fontSize="small" /> },
];

/**
 * Live progress for the extraction call. Every state shown here comes from a stage event the
 * server actually sent, so a tick means that step really finished — no timed guesswork.
 */
const ExtractionProgress: React.FC<{ stage: string; retrying: boolean; elapsed: number }> = ({
  stage,
  retrying,
  elapsed,
}) => {
  const currentIndex = STAGES.findIndex((s) => s.key === stage);

  return (
    <Box
      sx={{
        border: '1px solid var(--color-border-light)',
        borderRadius: 'var(--border-radius-lg)',
        bgcolor: 'var(--color-background-page)',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
          Building your guide
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {elapsed}s elapsed
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {STAGES.map((s, i) => {
          const done = currentIndex > i;
          const active = currentIndex === i;
          return (
            <Box key={s.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, mt: '2px' }}>
                {done ? (
                  <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: 'var(--color-success)' }} />
                ) : active ? (
                  <CircularProgress size={14} />
                ) : (
                  <RadioButtonUncheckedOutlinedIcon sx={{ fontSize: 18, color: 'var(--color-text-disabled)' }} />
                )}
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: active ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
                    color: done || active ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                  }}
                >
                  {s.label}
                </Typography>
                {active && (
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                    {s.detail}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {retrying && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--color-text-secondary)' }}>
          Some points from your document were missing, so it is running a second attempt to capture
          them. This roughly doubles the wait.
        </Typography>
      )}
    </Box>
  );
};

/**
 * Two different jobs behind one route.
 *
 * /guides/new is an IMPORT: pick a source, watch it extract, review. That flow
 * stays as it is below.
 *
 * /guides/:id is an EDITOR, and now uses the shared editor shell — a canvas of
 * the guide's own sections, an inspector, and publish gated on the things that
 * would break the public page.
 */
const GuideEditor: React.FC = () => {
  const { guideId } = useParams<{ guideId: string }>();
  const isEdit = !!guideId;
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [sourceMode, setSourceMode] = useState(MODE_UPLOAD);
  const [file, setFile] = useState<File | ''>('');
  /*
   * What the cheap half of the pipeline found, before spending four to six minutes on the model.
   *
   * Reading the document and storing its images is fast, deterministic and the part that fails
   * quietly. Running it on drop turns "a picture is missing from the finished guide" into "this
   * document has two images that cannot be stored", said before anyone has waited for anything.
   */
  const [preflight, setPreflight] = useState<{
    fileName: string;
    words: number;
    headings: number;
    tables: number;
    imagesStored: number;
    imagesFailed: number;
    readable: boolean;
    images: { index: number; url?: string; error?: string }[];
  } | null>(null);
  const [preflighting, setPreflighting] = useState(false);
  const [sourceText, setSourceText] = useState('');
  const [pageType, setPageType] = useState('hiring-guide');
  const [extracting, setExtracting] = useState(false);
  // Current stage streamed by the server, plus whether it is on its second attempt.
  const [stage, setStage] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [importError, setImportError] = useState('');
  /** A renamed slug or an unsaved draft: kept on screen, because it changes what Save does. */
  const [importNotice, setImportNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [populated, setPopulated] = useState(false);
  // Source points the deterministic coverage check could not find in the extracted guide.
  const [uncoveredPoints, setUncoveredPoints] = useState<string[]>([]);
  // Top-level keys the guide renderer does not read, e.g. a misspelt section name. Content parked
  // under one of these is in the JSON but will never appear on the live page.
  const [unknownSections, setUnknownSections] = useState<string[]>([]);
  /*
   * Images in the document that could not be stored.
   *
   * A picture missing from a finished guide is invisible as a failure: it looks exactly like a
   * document that had no picture. This was a log line only, so the one person who could act on it
   * was the one person who would never see it.
   */
  const [imageErrors, setImageErrors] = useState<{ index: number; error?: string }[]>([]);
  /*
   * Stored fine, then lost by the model. A different failure from imageErrors and a different fix:
   * these URLs are live, so the operator can paste them back rather than re-import.
   */
  const [droppedImages, setDroppedImages] = useState<{ index: number; url?: string }[]>([]);
  /** The structured generator's problems, notes and cost. Only ever on screen: nothing stores it. */
  const [report, setReport] = useState<ImportReportData | null>(null);
  /** The structured body is edited field by field; this swaps in the raw JSON for the rare case. */
  const [rawJson, setRawJson] = useState(false);
  /** The record /guides/extract created and wrote the generated guide onto. */
  const [importedGuideId, setImportedGuideId] = useState<string | null>(null);

  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', datePublished: '', status: 'DRAFT' });
  const [contentJson, setContentJson] = useState('');
  /**
   * The generated body.
   *
   * This page had no such state at all, so the HTML the generator produced was read off the
   * response, displayed nowhere and dropped on save. Every import from this screen stored an
   * envelope with no body, and because nothing was sent the publish gate fell back to the template
   * check and refused with "missing hook, roleOverview, hiringProcess" for sections an HTML guide
   * does not and should not have.
   */
  const [contentHtml, setContentHtml] = useState('');
  const [jsonError, setJsonError] = useState('');

  const setField = (name: string, value: any) => setForm((p) => ({ ...p, [name]: value }));

  /*
   * The slug is taken by a guide that is not this one.
   *
   * The editor shell has always blocked this; the create screen never did, and create is the
   * surface that collides most. The backend used to resolve a slug clash by updating the guide that
   * already held it, so the operator's new guide never appeared at its own URL and somebody else's
   * was overwritten. The mutation now refuses that write, and this says so before the round trip,
   * while the operator is still looking at the field.
   *
   * Asked of the server by slug (see useSlugAvailability), excluding only the import's own row.
   * It used to read a list capped at 500 guides and skip imports altogether, so an imported guide
   * whose slug belonged to a different guide was never flagged.
   */
  const slugCheck = useSlugAvailability(isEdit ? '' : form.slug, importedGuideId);
  const slugTaken = slugCheck.taken;

  // Generation runs for minutes and this screen is the only place its warnings are shown.
  useBeforeUnloadWhile(extracting);

  useEffect(() => {
    if (!isEdit) return;
    axios
      .get(`/api/guides/${guideId}`)
      .then(({ data }) => {
        setForm({
          title: data.title ?? '',
          slug: data.slug ?? '',
          excerpt: data.excerpt ?? '',
          datePublished: data.datePublished ?? '',
          status: data.status ?? 'DRAFT',
        });
        setPageType(data.pageType ?? 'hiring-guide');
        setContentJson(data.contentJson ?? '');
        setPopulated(true);
      })
      .catch(() => showToast('Failed to load guide', 'error'));
  }, [isEdit, guideId, showToast]);

  // Elapsed counter: a minute of silence needs a sign of life, and this one is real.
  useEffect(() => {
    if (!extracting) {
      setElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [extracting]);

  const isPasting = sourceMode === MODE_PASTE;
  const hasSource = isPasting ? sourceText.trim().length > 0 : !!file;

  /**
   * Check the document the moment it is dropped, before anyone commits to an AI run.
   *
   * The endpoint has existed since the import pipeline was written and nothing has ever called it,
   * so every failure it was built to catch early was still being discovered late.
   */
  const runPreflight = async (f: File) => {
    setPreflight(null);
    setPreflighting(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch('/api/guides/preflight', { method: 'POST', body: fd });
      if (!res.ok) return; // Never block the import on the check: extraction reports its own errors.
      setPreflight(await res.json());
    } catch {
      // Same reasoning. A preflight that cannot run is not a reason to stop someone importing.
    } finally {
      setPreflighting(false);
    }
  };

  const onExtract = async () => {
    if (!hasSource) {
      return showToast(isPasting ? 'Paste the guide text first' : 'Choose a file first', 'error');
    }
    setExtracting(true);
    setImportError('');
    setImportNotice('');
    setStage('reading');
    setRetrying(false);
    setUncoveredPoints([]);
    setUnknownSections([]);
    setReport(null);
    try {
      const fd = new FormData();
      if (isPasting) fd.append('text', sourceText.trim());
      else fd.append('file', file);
      fd.append('pageType', pageType);

      // The route streams its stages as newline-delimited JSON, so this reads the body rather
      // than using axios (which would only resolve once the whole ~60s call finished).
      const res = await fetch('/api/guides/extract', { method: 'POST', body: fd });
      if (!res.ok || !res.body) {
        // Validation failures answer before streaming starts, so they are still plain JSON.
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || 'Extraction failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result: any = null;
      let streamError = '';
      let failedImages: { index: number; error?: string }[] = [];
      let slugNotice = '';
      let doneEvent: Record<string, any> | null = null;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // keep the trailing partial line for the next chunk
        for (const line of lines) {
          if (!line.trim()) continue;
          const evt = JSON.parse(line);
          if (evt.stage === 'done') {
            result = evt.guide;
            doneEvent = evt;
            failedImages = evt.imageErrors ?? [];
            setDroppedImages(evt.imagesDropped ?? []);
            /*
             * The record the route already wrote the generated guide onto.
             *
             * This was ignored, so Save POSTed a NEW guide under the model's slug and the import
             * record was orphaned at `importing-...` on every run. Keeping it means Save updates
             * the row that already holds the content, and the archived source stays attached.
             */
            if (evt.guideId) setImportedGuideId(evt.guideId);
            /*
             * The model's slug belonged to another guide, so the draft was saved as `slug-2` (or
             * the next free one). Said out loud: a silent rename would leave the operator
             * publishing to a URL they never chose.
             */
            if (evt.slugRenamed) slugNotice = `/${evt.slugRenamed.from} already belongs to another guide, so this draft was saved as /${evt.slugRenamed.to}. Change the slug below if you want a different one.`;
            if (evt.saveError) slugNotice = `The draft could not be saved automatically (${evt.saveError}). Review it below and press Save draft.`;
          } else if (evt.stage === 'error') streamError = evt.error;
          else if (evt.stage === 'retrying') {
            setRetrying(true);
            setStage('extracting');
          } else if (evt.stage === 'working') {
            /*
             * A heartbeat, not a stage.
             *
             * The route emits one every fifteen seconds so the connection is never silent long
             * enough for a proxy to cut it. Passing it to setStage was a real bug: the checklist
             * finds the active step with STAGES.findIndex, an unknown key returns -1, and every
             * fifteen seconds the whole list went back to looking un-started. On screen that reads
             * as the import looping back to "Reading your document" forever, while the extraction
             * was in fact running normally the whole time.
             */
            // Nothing to update: the local ticker already shows elapsed time. What matters is
            // that this never reaches setStage.
          } else setStage(evt.stage);
        }
      }

      if (streamError) throw new Error(streamError);
      if (!result) throw new Error('Extraction returned no guide');

      setForm((p) => ({ ...p, title: result.title ?? '', slug: result.slug ?? '', excerpt: result.excerpt ?? '' }));
      setPageType(result.pageType ?? pageType);
      setContentJson(result.contentJson ? JSON.stringify(JSON.parse(result.contentJson), null, 2) : '');
      setContentHtml(result.contentHtml ?? '');
      const imported = reportFromDone(doneEvent);
      setReport(imported);
      setRawJson(false);
      const cost = imported?.usage ? ` (${formatCost(imported.usage.estimatedCostUsd)})` : '';
      const uncovered: string[] = result.uncoveredPoints ?? [];
      const unknown: string[] = result.unknownSections ?? [];
      setUncoveredPoints(uncovered);
      setUnknownSections(unknown);
      setImageErrors(failedImages);
      setPopulated(true);
      if (slugNotice) {
        setImportNotice(slugNotice);
        showToast(slugNotice, 'warning');
      } else {
        showToast(
          uncovered.length > 0
            ? `Guide drafted${cost}, but ${uncovered.length} point(s) from your document may be missing. Review before publishing.`
            : imported?.problems.length
              ? `Guide drafted${cost}, with ${imported.problems.length} thing(s) to fix before publishing.`
              : `Guide drafted from your document${cost}. Review and publish.`,
          uncovered.length > 0 || imported?.problems.length ? 'warning' : 'success',
        );
      }
    } catch (e: any) {
      /*
       * Kept on screen, not just toasted.
       *
       * An import is minutes long and an operator is rarely watching the whole time. A toast is
       * gone in seconds, so a failure that happened while they were in another tab left the page
       * looking merely idle, with no way to tell "it broke" from "it is still going".
       */
      setImportError(e?.message || 'Extraction failed');
      showToast(e?.message || 'Extraction failed', 'error');
    } finally {
      setExtracting(false);
      setStage('');
      setRetrying(false);
    }
  };

  const validateJson = useCallback((value: string) => {
    try {
      JSON.parse(value);
      setJsonError('');
      return true;
    } catch (e: any) {
      setJsonError(e?.message || 'Invalid JSON');
      return false;
    }
  }, []);

  const formatJson = () => {
    try {
      setContentJson(JSON.stringify(JSON.parse(contentJson), null, 2));
      setJsonError('');
    } catch (e: any) {
      setJsonError(e?.message || 'Invalid JSON');
    }
  };

  const save = async (status: 'DRAFT' | 'ACTIVE') => {
    if (!form.title.trim() || !form.slug.trim()) return showToast('Title and slug are required', 'error');
    if (slugTaken) {
      return showToast(
        `The slug "${form.slug.trim()}" already belongs to another guide. Choose a different one, or open that guide to edit it.`,
        'error',
      );
    }
    if (!validateJson(contentJson)) return showToast('Fix the JSON before saving', 'error');
    /*
     * The envelope, on publish only. A draft is deliberately allowed through so the gaps can be
     * filled in the editor.
     *
     * Neither of these lives in the body, so the required-sections check never asked for them, and
     * an HTML-authored guide skips that check entirely. The site fills both from the document when
     * the JSON has no `seo` block, which is why the excerpt and date fields count as the answer. The
     * server refuses the same publish; this says so before the round trip.
     */
    if (status === 'ACTIVE') {
      if (!(form.excerpt ?? '').trim()) {
        return showToast(
          'Add an excerpt before publishing: it is the description on the public card and in search results.',
          'error',
        );
      }
      if (!(form.datePublished ?? '').trim()) {
        return showToast(
          'Add a publish date before publishing: the page shows it and Google reads it.',
          'error',
        );
      }
    }
    setSaving(true);
    try {
      const fd = new FormData();
      // Archive whatever the guide was built from. Pasted text is wrapped in a .txt File so it
      // travels the same S3 archive path as an uploaded document, keeping provenance for both.
      const slugForFile = form.slug.trim();
      if (isPasting && sourceText.trim()) {
        fd.append('file', new File([sourceText.trim()], `${slugForFile}.txt`, { type: 'text/plain' }));
      } else if (file) {
        fd.append('file', file);
      }
      fd.append('slug', slugForFile);
      fd.append('pageType', pageType);
      fd.append('title', form.title.trim());
      fd.append('excerpt', form.excerpt ?? '');
      fd.append('datePublished', form.datePublished ?? '');
      fd.append('contentJson', contentJson);
      // Always sent, including empty: the field is what decides which publish gate applies, and
      // omitting it is what made an HTML guide get judged as a template one.
      fd.append('contentHtml', contentHtml);
      fd.append('status', status);
      // An import already has a record; only a hand-authored guide is genuinely new.
      const targetId = isEdit ? guideId : importedGuideId;
      if (targetId) await axios.put(`/api/guides/${targetId}`, fd);
      else await axios.post('/api/guides', fd);
      showToast(status === 'ACTIVE' ? 'Guide published' : 'Draft saved', 'success');
      navigate('/guides');
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Failed to save guide', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Existing guides open in the editor shell. Declared after every hook above
  // so the hook order is identical on both paths.
  if (isEdit) return <GuideEditorShell guideId={guideId as string} />;

  /*
   * Create pages are PageHero + sections, matching /jobs/new and /companies/new.
   *
   * This branch used to render `CRMPageTemplate` — the CRM *list* layout, complete with a filter
   * sidebar it never populated — around what is a create form. `isEdit` returns above, so
   * everything below is the new-guide path only, and every `isEdit &&` that used to appear in here
   * was unreachable.
   */
  return (
    <Box className="cc-create">
      <PageHero
        kicker="New guide"
        title="Import a document and turn it into a guide."
        text="Upload the source, review the sections that were extracted, then publish from the guide's own editor."
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 900 }}>
        <DetailSection title="Source">
          {(
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SelectFormField required label="Guide type" name="pageType" options={PAGE_TYPE_OPTIONS} value={pageType} onChange={(_n, v) => setPageType(String(v))} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CRMTabs sticky={false} className="crm-tab-bar--embedded" tabs={SOURCE_TABS} value={sourceMode} onChange={setSourceMode} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {isPasting ? (
                  <FormField
                    required
                    label="Guide text"
                    name="sourceText"
                    value={sourceText}
                    onChange={(_n, v) => setSourceText(String(v))}
                    disabled={extracting}
                    multiline
                    rows={14}
                    placeholder="Paste the full text of the guide here. Headings and paragraphs help the AI structure it accurately."
                    helperText={
                      sourceText.trim()
                        ? `${sourceText.trim().length.toLocaleString()} characters`
                        : 'Plain text. The AI reads this the same way it reads an uploaded document.'
                    }
                  />
                ) : (
                  <FileUpload
                    onFileDrop={(f: File) => {
                      setFile(f);
                      void runPreflight(f);
                    }}
                    file={file}
                    supportedFileTypes=".docx,.pdf,.txt"
                    maxFileSize={15 * 1024 * 1024}
                    disabled={extracting}
                    onDelete={() => {
                      setFile('');
                      setPreflight(null);
                    }}
                    title="Upload a Word, PDF, or text document"
                  />
                )}
                {preflighting && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'var(--tx-low)' }}>
                    Reading the document...
                  </Typography>
                )}
                {preflight && (
                  <Alert
                    severity={
                      !preflight.readable || preflight.imagesFailed > 0 ? 'warning' : 'info'
                    }
                    sx={{ mt: 1, borderRadius: 'var(--border-radius-lg)' }}
                  >
                    <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
                      {preflight.readable
                        ? `${preflight.words.toLocaleString()} words, ${preflight.headings} heading(s), ${preflight.tables} table(s)`
                        : 'No readable text in this document'}
                    </AlertTitle>
                    <Typography variant="body2">
                      {!preflight.readable
                        ? 'The AI has nothing to work from. A scanned PDF is images only; export it as .docx or paste the text instead.'
                        : preflight.imagesFailed > 0
                          ? `${preflight.imagesStored} image(s) stored, ${preflight.imagesFailed} could not be. Extracting now would produce a guide missing them.`
                          : preflight.imagesStored > 0
                            ? `${preflight.imagesStored} image(s) stored and publicly readable.`
                            : 'No images in this document.'}
                    </Typography>
                    {preflight.images.some((i) => i.error) && (
                      <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                        {preflight.images
                          .filter((i) => i.error)
                          .map((i) => (
                            <Typography key={i.index} component="li" variant="body2">
                              Image {i.index}: {i.error}
                            </Typography>
                          ))}
                      </Box>
                    )}
                  </Alert>
                )}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  disabled={!hasSource || extracting}
                  onClick={onExtract}
                >
                  {extracting ? 'Building your guide...' : 'Extract with AI'}
                </Button>
              </Grid>
              {extracting && (
                <Grid size={{ xs: 12 }}>
                  <ExtractionProgress stage={stage} retrying={retrying} elapsed={elapsed} />
                </Grid>
              )}
              {!extracting && importError && (
                <Grid size={{ xs: 12 }}>
                  {/* Stays until dismissed or the next attempt. The source is still attached
                      above, so retrying is one click and needs no re-upload. */}
                  <Alert
                    severity="error"
                    onClose={() => setImportError('')}
                    sx={{ borderRadius: 'var(--border-radius-lg)' }}
                  >
                    <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
                      The import did not finish
                    </AlertTitle>
                    {importError}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DetailSection>

        {importNotice && (
          <Alert
            severity="warning"
            icon={<ReportProblemOutlinedIcon fontSize="small" />}
            onClose={() => setImportNotice('')}
            sx={{ borderRadius: 'var(--border-radius-lg)' }}
          >
            {importNotice}
          </Alert>
        )}

        {report && <ImportReport report={report} />}

        {unknownSections.length > 0 && (
          <Alert
            severity="warning"
            icon={<ReportProblemOutlinedIcon fontSize="small" />}
            onClose={() => setUnknownSections([])}
            sx={{ borderRadius: 'var(--border-radius-lg)' }}
          >
            <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
              {unknownSections.length} section(s) will not appear on the live page
            </AlertTitle>
            <Typography variant="body2">
              The guide renderer does not read{' '}
              <strong>{unknownSections.join(', ')}</strong>. This is usually a near-miss on a
              section name (for example <code>whyItMatter</code> instead of{' '}
              <code>whyItMatters</code>). Move its content into the right section below (it is
              shown there as JSON), or it is lost.
            </Typography>
          </Alert>
        )}

        {uncoveredPoints.length > 0 && (
          <Alert
            severity="warning"
            icon={<ReportProblemOutlinedIcon fontSize="small" />}
            onClose={() => setUncoveredPoints([])}
            sx={{ borderRadius: 'var(--border-radius-lg)' }}
          >
            <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
              {uncoveredPoints.length} point(s) from your document may be missing
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              These lines were not found in the generated guide. Add anything genuinely missing to
              the content below before publishing. Reworded content can show up here too, so check
              each one.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {uncoveredPoints.map((point, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {point}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}

        {imageErrors.length > 0 && (
          <Alert
            severity="warning"
            icon={<ReportProblemOutlinedIcon fontSize="small" />}
            onClose={() => setImageErrors([])}
            sx={{ borderRadius: 'var(--border-radius-lg)' }}
          >
            <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
              {imageErrors.length} image(s) in your document could not be stored
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The guide was drafted without them. The reason is reported verbatim below, because the
              difference between a rejected format and a rejected upload is the whole diagnosis.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {imageErrors.map((image) => (
                <Typography key={image.index} component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Image {image.index}: {image.error}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}

        {droppedImages.length > 0 && (
          <Alert
            severity="warning"
            icon={<ReportProblemOutlinedIcon fontSize="small" />}
            onClose={() => setDroppedImages([])}
            sx={{ borderRadius: 'var(--border-radius-lg)' }}
          >
            <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
              {droppedImages.length} image(s) were stored but left out of the draft
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              These uploaded successfully and the model was given them in position, but they are
              not in the generated body. Nothing about the result would show that, which is why it
              is reported here: a missing picture reads as a document that never had one. The links
              are live, so you can put them back as a figure or a section image below.
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {droppedImages.map((image) => (
                <Typography key={image.index} component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Image {image.index}: <code>{image.url}</code>
                </Typography>
              ))}
            </Box>
          </Alert>
        )}

        {(populated || isEdit) && (
          <>
            <DetailSection title="Guide details">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <FormField label="Title" name="title" value={form.title} onChange={setField} required />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormField
                    label="Slug"
                    name="slug"
                    value={form.slug}
                    onChange={setField}
                    required
                    error={slugTaken}
                    helperText={
                      slugTaken
                        ? `Already used by another guide${slugCheck.takenBy?.title ? ` ("${slugCheck.takenBy.title}")` : ''}. Pick a different slug, or open that guide to edit it.`
                        : 'URL segment, e.g. how-to-become-a-gemologist'
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormField
                    required={false}
                    label="Excerpt"
                    name="excerpt"
                    value={form.excerpt}
                    onChange={setField}
                    multiline
                    rows={4}
                    helperText="The short summary shown on the guide cards and in listings."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <SelectFormField required={false} label="Type" name="pageType" options={PAGE_TYPE_OPTIONS} value={pageType} onChange={(_n, v) => setPageType(String(v))} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <SelectFormField label="Status" name="status" options={STATUS_OPTIONS} value={form.status} onChange={setField}  required={false} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormField label="Date published" name="datePublished" type="date" value={form.datePublished} onChange={setField}  required={false} />
                </Grid>
              </Grid>
            </DetailSection>

            <DetailSection title="Hero image">
              <HeroImageField
                contentJson={contentJson}
                onChange={setContentJson}
                slug={form.slug}
              />
            </DetailSection>

            {/* The body, in whichever form this guide has one.
                A generated guide's body is HTML and its contentJson holds only the envelope, so
                showing the JSON editor for it pointed the operator at a field with nothing to
                edit, while the thing they had just generated was invisible and unsaved. */}
            {contentHtml.trim() ? (
              <DetailSection title="Content (HTML)">
                <HtmlBodyEditor
                  title={form.title}
                  slug={form.slug}
                  value={contentHtml}
                  onChange={setContentHtml}
                />
              </DetailSection>
            ) : (
            <DetailSection
              title="Content"
              headerAction={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {rawJson && <Button size="small" onClick={formatJson}>Format</Button>}
                  <Button
                    size="small"
                    onClick={() => {
                      // Leaving the raw view with JSON that does not parse would hand the field
                      // editor nothing to open; say so instead.
                      if (rawJson && !validateJson(contentJson)) return;
                      setRawJson((v) => !v);
                    }}
                  >
                    {rawJson ? 'Edit as fields' : 'Edit as JSON'}
                  </Button>
                </Box>
              }
            >
              {rawJson ? (
                <FormField
                  required={false}
                  label=""
                  name="contentJson"
                  value={contentJson}
                  onChange={(_n, v) => { setContentJson(String(v)); if (jsonError) setJsonError(''); }}
                  multiline
                  rows={18}
                  error={!!jsonError}
                  helperText={jsonError || 'The full guide body (ResourcePageData). Edit carefully: this renders the live page.'}
                  textFieldProps={{
                    onBlur: () => validateJson(contentJson),
                    slotProps: {
                      input: { sx: { fontFamily: 'var(--font-family-monospace)', fontSize: 'var(--font-size-xs)' } },
                    },
                  }}
                />
              ) : (
                <StructuredBodyEditor
                  contentJson={contentJson}
                  pageType={pageType}
                  slug={form.slug}
                  onChange={setContentJson}
                />
              )}
            </DetailSection>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" disabled={saving} onClick={() => save('DRAFT')}>Save draft</Button>
              <Button variant="contained" disabled={saving} onClick={() => save('ACTIVE')}>Publish</Button>
              <Button variant="text" onClick={() => navigate('/guides')}>Cancel</Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default GuideEditor;
